import { apiClient as api, authHeaders } from './api';

const BASE = '/api/v3/chat';

export interface ChatMessageRequest {
  message: string;
  sessionId?: string;
  studioId?: string;
  agentId?: string;
  context?: Record<string, unknown>;
}

export interface ChatStreamChunk {
  type: 'token' | 'message' | 'tool_call' | 'tool_result' | 'error' | 'done';
  content?: string;
  metadata?: Record<string, unknown>;
}

export interface SendMessageOptions {
  signal?: AbortSignal;
  onChunk?: (chunk: ChatStreamChunk) => void;
}

/**
 * Chat over SSE. POST a message, consume a stream of token / tool_call / done
 * events. BE surface lands in B24; the shape mirrors the planned contract so
 * F18 (agent-bar chat) can wire against this without a service rewrite.
 */
export const ChatService = {
  async sendMessage(request: ChatMessageRequest, options: SendMessageOptions = {}): Promise<string> {
    const baseURL = api.defaults.baseURL ?? '';
    const response = await fetch(`${baseURL}${BASE}/messages`, {
      method: 'POST',
      body: JSON.stringify(request),
      signal: options.signal,
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders(),
      },
    });
    if (!response.ok || !response.body) {
      throw new Error(`Chat message failed: ${response.status} ${response.statusText}`);
    }
    return consumeSseMessage(response.body, options.onChunk);
  },
};

async function consumeSseMessage(
  body: ReadableStream<Uint8Array>,
  onChunk?: (chunk: ChatStreamChunk) => void,
): Promise<string> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let assembled = '';

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    const events = buffer.split('\n\n');
    buffer = events.pop() ?? '';

    for (const raw of events) {
      const dataLine = raw.split('\n').find(line => line.startsWith('data:'));
      if (!dataLine) continue;
      const payload = dataLine.slice(5).trim();
      if (!payload) continue;
      try {
        const chunk = JSON.parse(payload) as ChatStreamChunk;
        onChunk?.(chunk);
        if (chunk.type === 'token' && chunk.content) assembled += chunk.content;
        if (chunk.type === 'message' && chunk.content) assembled = chunk.content;
      } catch {
        // Non-JSON keepalive — ignore.
      }
    }
  }
  return assembled;
}
